import {defineStore} from "pinia";
import {ref, Ref} from "vue";
import {
    IAccount,
    IAccountConfirm,
    IAccountEnd,
    IAccountFilter,
    IAccountStart
} from "@/features/account/account.types";
import {accountService} from "@/features/account/account.service";
import {ITableHeader} from "@/types/table";
import {useAlert} from "@/utils/alert";

const {show} = useAlert()
export const useAccountStore = defineStore("account", () => {
    const accountsForFilter: IAccount[] = [];
    const commonFilter = ref<IAccountFilter>({
        date_from: '',
        date_to: '',
    })

    const headers: ITableHeader[] = [
        {
            name: "id",
            label: "No",
        },
        {
            name: "account",
            label: "Account",
        },
        {
            name: "all",
            label: "All messages",
            panel: ["allMessages"]
        },
        {
            name: "text",
            label: "Text",
            panel: ["allMessages", "text"]
        },
        {
            name: "voice",
            label: "Voice & Document",
            panel: ["allMessages", "voice"]
        },
        {
            name: "call",
            label: "Call",
            panel: ["allMessages", "call"]
        },
        {
            name: "photo",
            label: "Photo & Video",
            panel: ["allMessages", "photo"]
        },
        {
            name: "all_errors",
            label: "All errors",
            type: "error",
            panel: ["allMessages", "allErrors"]
        },
        {
            name: "reply_time",
            label: "Reply Time",
            type: "error",
            panel: ["allMessages", "replyTime"]
        },
        {
            name: "unanswered",
            label: "Unanswered",
            type: "error",
            panel: ["allMessages", "unanswered"]
        },
        {
            name: "deleted",
            label: "Deleted",
            type: "error",
            panel: ["allMessages", "deleted"]
        },
        {
            name: "edited",
            label: "Edited",
            type: "error",
            panel: ["allMessages", "edited"]
        },
        {
            name: "actions",
            label: "Actions",
        },
    ];

    async function getAccounts(
        loading: Ref<boolean>
    ): Promise<IAccount[]> {
        try {
            loading.value = true
            const {data} = await accountService.getAccounts()
            return data.accounts
        } finally {
            loading.value = false
        }
    }

    async function startAccount(
        loading: Ref<boolean>,
        payload: IAccountStart,
        cl: any
    ) {
        loading.value = true
        await accountService.startAccount(payload)
            .then((result) => {
                cl(result)
            }).catch(error => {
                show(error, "error");
            }).finally(() => {
                loading.value = false
            })
    }

    async function reconnectAccount(
        loading: Ref<boolean>,
        id: string,
        payload: IAccountStart,
        cl: any
    ) {
        loading.value = true
        await accountService.accountReconnect(id, payload)
            .then((result) => {
                cl(result)
            }).catch(error => {
                show(error, "error");
            }).finally(() => {
                loading.value = false
            })
    }

    async function confirmAccount(
        loading: Ref<boolean>,
        payload: IAccountConfirm,
        cl: any
    ) {
        loading.value = true
        await accountService.confirmAccount(payload)
            .then((result) => {
                cl(result)
            }).catch(error => {
                show(error, "error");
            }).finally(() => {
                loading.value = false
            })
    }

    async function confirmReconnect(
        loading: Ref<boolean>,
        id: string,
        payload: IAccountConfirm,
        cl: any
    ) {
        loading.value = true
        await accountService.confirmReconnect(id, payload)
            .then((result) => {
                cl(result)
            }).catch(error => {
                show(error, "error");
            }).finally(() => {
                loading.value = false
            })
    }

    async function accountActivity(
        loading: Ref<boolean>,
        sessionName: string
    ): Promise<IAccount[]> {
        try {
            loading.value = true
            const {data} = await accountService.accountActivity(sessionName)
            return data
        } catch (err) {
            throw err
        } finally {
            loading.value = false
        }
    }

    async function endAccount(
        loading: Ref<boolean>,
        sessionName: string,
        payload: IAccountEnd
    ): Promise<IAccount[]> {
        try {
            loading.value = true
            const {data} = await accountService.endAccount(sessionName, payload)
            return data
        } finally {
            loading.value = false
        }
    }

    return {
        accountsForFilter,
        commonFilter,
        headers,
        getAccounts,
        startAccount,
        reconnectAccount,
        confirmReconnect,
        confirmAccount,
        accountActivity,
        endAccount
    };
});
